import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import {
    GetTemplatesNamesAndIdsDto,
    GetTranslationsDto,
    IGroupEntityDTO,
    ITemplateEntityDTO,
    TemplateType,
    UpdateTranslationsDto,
} from '@modeso/types__aumico-lib-templates-be';
import Debug from 'debug';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AumicoTemplatesConfigService } from '../aumico-templates.config';
import { TemplatesTranslationRequest } from '../interfaces/templates-translation-request.interface';
import { TemplatesTranslationResponse } from '../interfaces/templates-translation-response.interface';

const debug = Debug('modeso:aumico-templates:AumicoTemplatesService');
const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
};

@Injectable()
export class AumicoTemplatesService {
    private server = '';
    private microserviceName = 'templates';
    private readonly groupRoute = '/group';
    private readonly translations = '/translations';
    private readonly fallback = '/fallback';
    private readonly route = '/';

    constructor(
        private http: HttpClient,
        @Inject(AumicoTemplatesConfigService) private config,
        private coreProvider: AumicoCoreProvider,
    ) {
        this.server = config.apiGateway;
    }

    //Templates
    getTemplates(): Observable<ITemplateEntityDTO[]> {
        const url = `${this.server}${this.microserviceName}`;
        this.coreProvider.dispatchBlockingRequest('TemplateFe_getTemplates');
        return this.http
            .get<ITemplateEntityDTO[]>(url, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('TemplateFe_getTemplates')));
    }

    getTemplateByLegalFormAndAccountBasis(payload: {
        templateType: string;
        accountBasis: string;
        legalForm: string;
    }): Observable<ITemplateEntityDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${payload.templateType}${this.route}${payload.legalForm}${this.route}${payload.accountBasis}`;
        this.coreProvider.dispatchBlockingRequest('TemplateFe_getTemplateByLegalFormAndAccountBasis');
        return this.http
            .get<ITemplateEntityDTO>(url, httpOptions)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('TemplateFe_getTemplateByLegalFormAndAccountBasis'),
                ),
            );
    }

    getTemplateById(id: string): Observable<ITemplateEntityDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${id}`;
        this.coreProvider.dispatchBlockingRequest('TemplateFe_getTemplateById');
        return this.http
            .get<ITemplateEntityDTO>(url, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('TemplateFe_getTemplateById')));
    }

    createTemplate(payload: { template: ITemplateEntityDTO }): Observable<ITemplateEntityDTO> {
        const url = `${this.server}${this.microserviceName}`;
        return this.http.post<ITemplateEntityDTO>(url, payload.template, httpOptions);
    }

    updateTemplate(payload: { template: ITemplateEntityDTO; templateId: string }): Observable<ITemplateEntityDTO> {
        const url = `${this.server}${this.microserviceName}${this.route}${payload.templateId}`;
        return this.http.put<ITemplateEntityDTO>(url, payload.template, httpOptions);
    }

    //Groups
    getGroups(): Observable<IGroupEntityDTO[]> {
        const url = `${this.server}${this.microserviceName}${this.groupRoute}`;
        this.coreProvider.dispatchBlockingRequest('TemplateFe_getGroups');
        return this.http
            .get<IGroupEntityDTO[]>(url, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('TemplateFe_getGroups')));
    }

    createGroup(payload: { group: IGroupEntityDTO }): Observable<IGroupEntityDTO> {
        const url = `${this.server}${this.microserviceName}${this.groupRoute}`;
        return this.http.post<IGroupEntityDTO>(url, payload.group, httpOptions);
    }

    updateGroup(payload: { group: IGroupEntityDTO; groupId: string }): Observable<IGroupEntityDTO> {
        const url = `${this.server}${this.microserviceName}${this.groupRoute}${this.route}${payload.groupId}`;
        return this.http.put<IGroupEntityDTO>(url, payload.group, httpOptions);
    }

    deleteGroup(groupId: string): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.groupRoute}${this.route}${groupId}`;
        return this.http.delete<void>(url, httpOptions);
    }

    getAllTemplatesNamesAndIdsByTemplateType$(templateType: TemplateType): Observable<GetTemplatesNamesAndIdsDto[]> {
        const url = `${this.server}${this.microserviceName}${this.route}${templateType}${this.route}names-ids`;
        this.coreProvider.dispatchBlockingRequest('getAllTemplatesNamesAndIdsByTemplateType$');
        return this.http
            .get<GetTemplatesNamesAndIdsDto[]>(url)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('getAllTemplatesNamesAndIdsByTemplateType$'),
                ),
            );
    }

    public getTranslations(locales: string[], untranslated: boolean): Observable<GetTranslationsDto> {
        let url = `${this.server}${this.microserviceName}${this.translations}`;

        const queryParams = `locales[]=${locales.join('&locales[]=')}&untranslated=${untranslated}`;
        const urlWithParams = `${url}?${queryParams}`;

        this.coreProvider.dispatchBlockingRequest('getTranslations');

        return this.http
            .get<GetTranslationsDto>(urlWithParams, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getTranslations')));
    }

    public updateTranslations(translations: UpdateTranslationsDto): Observable<void> {
        const url = `${this.server}${this.microserviceName}${this.translations}`;
        this.coreProvider.dispatchBlockingRequest('updateTranslations');
        return this.http
            .patch<void>(url, translations, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateTranslations')));
    }

    public getTemplatesTargetTranslations(
        translationRequest: TemplatesTranslationRequest,
    ): Observable<TemplatesTranslationResponse> {
        const url = `${this.server}${this.microserviceName}${this.translations}${this.fallback}`;
        this.coreProvider.dispatchBlockingRequest('getTemplatesTargetTranslations');
        return this.http
            .post<TemplatesTranslationResponse>(url, translationRequest, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getTemplatesTargetTranslations')));
    }
}
